import { Card, DatePicker, Divider, Flex, Select, Table, TableProps } from 'antd';
import dayjs from 'dayjs';
import { FunctionComponent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// components
import Icon from 'utils/icon';
import StatusComponent from 'components/status/status';
import NoData from 'components/noData/noData';
import ListButton from 'components/listButton/listButton';

// models
import { Payment, PaymentsPayload, PaymentStatus, PaymentType } from '../models/payment';
import NotificationService from 'core/services/notification';
import { onExportPaymentInvoice, onGetPayments } from '../services/paymentsService';
import TableTotal from 'components/tableTotal/tableTotal';
import NumberElement from 'components/number/number';
import Riyal from 'components/riyal/riyal';
import PriceElement from 'components/price/price';
import { StatusColors } from 'utils/statusColors';
import { isRTL } from 'core/services/language';
import { downloadFile } from 'utils/downloadFile';

interface PaymentsPageProps {}

const PaymentsPage: FunctionComponent<PaymentsPageProps> = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const { RangePicker } = DatePicker;

	const [isLoading, setIsLoading] = useState<boolean>(true);

	const [dateRange, setDateRange] = useState<[string, string]>();
	const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | ''>('');
	const [paymentType, setPaymentType] = useState<PaymentType | ''>('');

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const [payments, setPayments] = useState<Payment[]>([]);

	const statusOptions = [
		{ value: '', label: t('COMMON.ALL') },
		{ value: 'Pending', label: t('COMMON.PENDING') },
		{ value: 'Completed', label: t('COMMON.COMPLETED') },
		{ value: 'Failed', label: t('COMMON.FAILED') },
	];

	const paymentTypesOptions = [
		{ value: '', label: t('COMMON.ALL') },
		{ value: 'WalletCharging', label: t('COMPANY_INFORMATION.WALLET_CHARGE') },
		{ value: 'PointWalletCharging', label: t('COMPANY_INFORMATION.POINT_WALLET_CHARGE') },
		{ value: 'BuyPlan', label: t('COMPANY_INFORMATION.BUY_PLAN') },
	];

	const columns: TableProps<Payment>['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: t('COMPANY_INFORMATION.PAYMENT_TYPE'),
			dataIndex: 'payment_type',
			key: 'payment_type',
			render: (_, record) => (isRTL() ? record.translated_payment_type.ar : record.translated_payment_type.en),
		},
		{
			title: t('COMPANY_INFORMATION.PLAN'),
			dataIndex: 'translated_pay_for',
			key: 'translated_pay_for',
			render: (pay_for) => <span>{isRTL() ? pay_for.ar : pay_for.en}</span>,
		},
		{
			title: t('COMMON.AMOUNT'),
			dataIndex: 'amount',
			key: 'amount',
			render: (amount, record) =>
				record?.currency === 'SAR' ? (
					<PriceElement value={amount.total} decimalScale={2} color="#333" />
				) : (
					<span>
						{record?.currency} <NumberElement value={amount.total} decimalScale={2} />
					</span>
				),
		},
		{
			title: t('COMMON.POINTS'),
			dataIndex: 'points',
			key: 'points',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusComponent text={t(`COMMON.${status.toUpperCase()}`)} color={StatusColors[status]} />,
		},
		{
			title: t('COMMON.CREATED_AT'),
			dataIndex: 'created_at',
			key: 'created_at',
			render: (date) => dayjs(date).format('DD/MM/YYYY, hh:mm A'),
		},
		{
			title: t('COMPANY_INFORMATION.INVOICE_NUMBER'),
			dataIndex: 'reference',
			key: 'reference',
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => {
				return record?.status == 'Completed' ? <ListButton type="download" onClick={() => downloadInvoice(record.id)} /> : null;
			},
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());

		getTransactionLogs();
	}, [paymentStatus, paymentType, dateRange]);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getTransactionLogs = async () => {
		try {
			setIsLoading(true);

			const payload: PaymentsPayload = {
				page: getCurrentPage(),
				status: paymentStatus || undefined,
				payment_type: paymentType || undefined,
				date_from: dateRange ? dayjs(dateRange[0]).format('YYYY-MM-DD') : undefined,
				date_to: dateRange ? dayjs(dateRange[1]).format('YYYY-MM-DD') : undefined,
			};

			const response = await onGetPayments(payload);

			setPayments(response.payment_invoices);
			setTotalRecords(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const payload: PaymentsPayload = {
				page: page,
				status: paymentStatus || undefined,
				payment_type: paymentType || undefined,
				date_from: dateRange ? dayjs(dateRange[0]).format('YYYY-MM-DD') : undefined,
				date_to: dateRange ? dayjs(dateRange[1]).format('YYYY-MM-DD') : undefined,
			};

			const response = await onGetPayments(payload);
			setPayments(response['payment_invoices']);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const downloadInvoice = async (id: number) => {
		try {
			const exportResponse = await onExportPaymentInvoice(id);

			if (exportResponse === null) {
				NotificationService.error(t('COMMON.ERROR'), t('COMMON.DOWNLOAD_ERROR'));
				return;
			}

			await downloadFile(exportResponse.download_url, exportResponse.filename || 'invoice.pdf');
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.DOWNLOAD_ERROR'));
		}
	};

	return (
		<>
			<Card>
				<Flex align="center" gap={20} wrap>
					<div>
						<p className="text-light fw-medium mb-1">{t('COMMON.DATE_RANGE')}</p>
						<RangePicker
							size="large"
							maxDate={dayjs(new Date())}
							format="DD MMM YYYY"
							suffixIcon={<Icon name="calendar" />}
							separator="-"
							inputReadOnly
							placeholder={[t('COMMON.START_DATE'), t('COMMON.END_DATE')]}
						/>
					</div>

					<div>
						<p className="text-light fw-medium mb-1">{t('COMMON.STATUS')}</p>
						<Select size="large" onChange={setPaymentStatus} options={statusOptions} defaultValue="" className="filled" />
					</div>
					<div>
						<p className="text-light fw-medium mb-1">{t('COMPANY_INFORMATION.PAYMENT_TYPE')}</p>
						<Select size="large" onChange={setPaymentType} options={paymentTypesOptions} defaultValue="" className="filled" />
					</div>
				</Flex>

				<Divider />

				<Table
					columns={columns}
					dataSource={payments}
					scroll={{ x: 600 }}
					rowKey="id"
					loading={isLoading}
					pagination={{
						pageSize: 25,
						total: totalRecords,
						showSizeChanger: false,
						current: currentPage,
						onChange: fetchMoreData,
						showTotal: (total) => <TableTotal total={total} />,
					}}
					locale={{ emptyText: <NoData /> }}
				/>
			</Card>
		</>
	);
};

export default PaymentsPage;
